import * as React from 'react';

/**
 * What a `<Label>` tells the control inside it: which elements name it, which
 * describe it, and whether it has an error. Each input type reads this and sets
 * its own attributes, so a caller only ever passes the label, help text and
 * error to the `<Label>`, and never sets them on the input as well.
 */
export type LabelContextValue = {
  /** The ids of the visible label text, as `aria-labelledby` takes them. */
  labelledBy: string;
  /** The ids of the help text and the error, when the label has either. */
  describedBy: string | undefined;
  /** Whether the label is showing an error. */
  invalid: boolean;
};

const LabelContext = React.createContext<LabelContextValue | null>(null);

export const LabelContextProvider = LabelContext.Provider;

/** The nearest `<Label>`'s context, or `null` outside of one. */
export const useLabelContext = (): LabelContextValue | null =>
  React.useContext(LabelContext);

/**
 * Stops the surrounding `<Label>` from naming anything inside. React context
 * crosses portals, so dialogs and popovers opened from inside a label would
 * otherwise hand its name, help text and error to their own fields.
 */
export const LabelBoundary = ({children}: {children: React.ReactNode}) => (
  <LabelContext.Provider value={null}>{children}</LabelContext.Provider>
);

/** Joins id references, dropping the empty ones, or `undefined` if none. */
export const joinIds = (
  ...ids: Array<string | false | null | undefined>
): string | undefined => ids.filter(Boolean).join(' ') || undefined;

type OwnLabelling = {
  'aria-describedby'?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  id?: string;
  invalid?: boolean;
};

export type LabelledControlProps = {
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  'aria-label': string | undefined;
  'aria-labelledby': string | undefined;
  id: string | undefined;
};

/**
 * The name, description and error state of a control, from its own props and
 * the surrounding `<Label>`. Spread the result onto the element that takes
 * focus.
 *
 * Inside a label, the control's name starts with the label text. A control that
 * shares the label with others (the hex field beside a color picker, the hours
 * of a timecode) passes its own `aria-label` or `aria-labelledby`, which is read
 * after it: "Accent color hex value". The control refers to itself for that, so
 * it gets an id. The label's help text and error describe it, and the label's
 * error makes it invalid, as does its own `invalid` prop.
 */
export const useLabelledControl = ({
  'aria-describedby': ownDescribedBy,
  'aria-label': ownLabel,
  'aria-labelledby': ownLabelledBy,
  id: ownId,
  invalid = false,
}: OwnLabelling): LabelledControlProps => {
  const context = useLabelContext();
  const generatedId = React.useId();

  if (!context) {
    return {
      'aria-describedby': ownDescribedBy,
      'aria-invalid': invalid || undefined,
      'aria-label': ownLabel,
      'aria-labelledby': ownLabelledBy,
      id: ownId,
    };
  }

  const selfId = ownLabel && !ownLabelledBy ? (ownId ?? generatedId) : null;
  return {
    'aria-describedby': joinIds(context.describedBy, ownDescribedBy),
    'aria-invalid': invalid || context.invalid || undefined,
    'aria-label': ownLabel,
    'aria-labelledby': joinIds(context.labelledBy, ownLabelledBy, selfId),
    id: selfId ?? ownId,
  };
};
